// =============================================================================
// Draft persistence — writes the form's RHF value to localStorage every time
// it changes (debounced by the caller), restores on demand. The key + TTL
// match the static form so users with a draft already saved get it back
// after the rewrite ships.
// =============================================================================

const KEY = "rt_sinai_draft_v2";
const TTL_MS = 24 * 60 * 60 * 1000;

export interface DraftEnvelope<T> {
  timestamp: number;
  values: T;
  version: number;
}

const SCHEMA_VERSION = 2; // bump to invalidate old drafts after breaking changes

export function saveDraft<T>(values: T): void {
  try {
    const envelope: DraftEnvelope<T> = {
      timestamp: Date.now(),
      values,
      version: SCHEMA_VERSION,
    };
    localStorage.setItem(KEY, JSON.stringify(envelope));
  } catch {
    /* ignore quota errors etc. */
  }
}

export function loadDraft<T>(): T | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope<T>;
    if (parsed?.version !== SCHEMA_VERSION) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (!parsed?.timestamp || Date.now() - Number(parsed.timestamp) > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed.values;
  } catch {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

export function hasDraft(): boolean {
  return loadDraft<unknown>() !== null;
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
